// UserGroupTable.js
import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { Trash3 } from "react-bootstrap-icons";
import TanstackTable from "components/elements/advance-table/TanstackTable";
import { modifyUserGroup } from "ServerAPI/serverAPI";

const UserGroupTable = ({
    data,
    onUserDeleted,
    userGroupId,
    cookies,
    showToast
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deletingUser, setDeletingUser] = useState(false);

    // Open delete confirmation modal
    const handleDeleteConfirmation = (userId) => {
        setUserToDelete(userId);
        setShowDeleteModal(true);
    };

    // Close delete modal and reset state
    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeletingUser(false);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (userToDelete) {
            handleDeleteUser(userToDelete);
        }
    };

    // Handle delete user with proper error handling and response checking
    const handleDeleteUser = async (userId) => {
        setDeletingUser(true);
        try {
            const payload = {
                userGroupId: userGroupId,
                operation: "remove",
                userIds: [userId],
                authorUserId: cookies.userId,
            };

            const response = await modifyUserGroup(payload);

            // Only proceed if response status is 200
            if (response.status === 200 || response.ok) {
                await onUserDeleted(); // Refresh the user list
                showToast("User removed successfully", "success");
                handleDeleteModalClose(); // Close modal only on success
            } else {
                console.error("Failed to delete user:", response);
                showToast(`Failed to delete user.`, "error");
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            showToast(
                error.message || "Failed to remove user. Please try again.",
                "error"
            );
        } finally {
            setDeletingUser(false);
        }
    };

    const usersColumns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>SN</span>
                ),
                size: 80,
            },
            {
                accessorKey: "name",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>Name</span>
                ),
                cell: ({ getValue }) => (
                    <div style={{ fontWeight: 500 }}>{getValue()}</div>
                ),
            },
            {
                accessorKey: "email",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>Email</span>
                ),
                cell: ({ getValue }) => (
                    <div style={{ color: "#6c757d", fontSize: "14px" }}>{getValue()}</div>
                ),
            },
            {
                accessorKey: "phoneNumber",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>Phone</span>
                ),
                cell: ({ getValue }) => (
                    <div style={{ color: "#6c757d", fontSize: "14px" }}>
                        {getValue() || "N/A"}
                    </div>
                ),
            },
            {
                accessorKey: "delete",
                header: () => (
                    <span style={{ fontWeight: "bold", color: "#224D99" }}>Action</span>
                ),
                size: 100,
                cell: ({ row }) => (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteConfirmation(row.original.userId)}
                        disabled={deletingUser}
                        title="Remove user"
                    >
                        <Trash3 size={14} className="me-1" />
                        Remove
                    </Button>
                ),
            },
        ],
        [deletingUser]
    );

    return (
        <>
            <div style={{ minHeight: "400px" }}>
                <TanstackTable
                    columns={usersColumns}
                    data={data}
                    pagination={true}
                    pageSize={10}
                />
            </div>

            {/* Remove User Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleDeleteModalClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ color: "#224D99" }}>Remove User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center p-3">
                        <div style={{ fontSize: "48px", color: "#ffc107" }}>⚠️</div>
                        <h5 className="mt-3 mb-3">Are you sure?</h5>
                        <p className="text-muted">
                            Do you really want to remove this user from the group?
                            This action cannot be undone.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleDeleteModalClose}
                        disabled={deletingUser}
                        style={{ minWidth: "100px" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleConfirmDelete}
                        disabled={deletingUser}
                        style={{ minWidth: "100px" }}
                    >
                        {deletingUser ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                />
                                Removing...
                            </>
                        ) : (
                            <>
                                <Trash3 size={14} className="me-1" />
                                Remove
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default UserGroupTable;